"use strict";
/**
 * NoLimitGamblePlugin
 * A plugin for gamble feature handling.
 *
 * The user (the game) is responsible for starting the feature when it's ready. Usually in the "done" slotState,
 * when other transitions (into fs, respin etc) is usually checked.
 *
 *
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.NoLimitGamblePlugin = void 0;
const NolimitApplication_1 = require("@nolimitcity/slot-launcher/bin/NolimitApplication");
const NolimitLauncher_1 = require("@nolimitcity/slot-launcher/bin/NolimitLauncher");
const SoundPlugin_1 = require("@nolimitcity/slot-launcher/bin/plugins/SoundPlugin");
const ApiPlugin_1 = require("@nolimitcity/slot-launcher/bin/plugins/ApiPlugin");
const KeypadPlugin_1 = require("@nolimitcity/slot-launcher/bin/plugins/KeypadPlugin");
const AutoPlayPlugin_1 = require("@nolimitcity/slot-launcher/bin/plugins/AutoPlayPlugin");
const ImgLoader_1 = require("@nolimitcity/slot-launcher/bin/loader/ImgLoader");
const GambleAssetConfig_1 = require("./config/GambleAssetConfig");
const FontLoader_1 = require("@nolimitcity/slot-launcher/bin/loader/FontLoader");
const OpenSans_1 = require("@nolimitcity/slot-launcher/bin/loader/font/OpenSans");
const APIEventSystem_1 = require("@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem");
const NoLimitGambleBaseView_1 = require("./view/NoLimitGambleBaseView");
const NoLimitGambleChoiceView_1 = require("./view/NoLimitGambleChoiceView");
const NoLimitGambleFiftyFiftyGambleView_1 = require("./view/NoLimitGambleFiftyFiftyGambleView");
const NoLimitGambleFeatureWinView_1 = require("./view/NoLimitGambleFeatureWinView");
const GambleGameMode_1 = require("./config/GambleGameMode");
const GambleEvents_1 = require("./config/GambleEvents");
const SlotStateHandler_1 = require("@nolimitcity/slot-launcher/bin/plugins/apiplugin/SlotStateHandler");
const gsap_1 = require("gsap");
const FontStatics_1 = require("@nolimitcity/slot-launcher/bin/loader/font/FontStatics");
const TemplateLoader_1 = require("@nolimitcity/slot-launcher/bin/loader/TemplateLoader");
const NoLimitGambleBgView_1 = require("./view/NoLimitGambleBgView");
const GSAPCompatabilityWrapper_1 = require("@nolimitcity/slot-launcher/bin/utils/GSAPCompatabilityWrapper");
class NoLimitGamblePlugin {
    /**
     * @constructs
     * @param config
     */
    constructor(config) {
        this.name = "NolimitGamblePlugin";
        this.DEFAULT_GAMBLE_CONFIG = {
            useDefaultAssets: true,
            winLoseTextStyle: new PIXI.TextStyle({
                dropShadow: true,
                dropShadowAlpha: 1.0,
                dropShadowAngle: -1.57,
                dropShadowBlur: 40,
                dropShadowColor: "#ffffff",
                dropShadowDistance: 0,
                fill: [
                    "#6f0000",
                    "red"
                ],
                fontSize: 100,
                lineJoin: "round",
                strokeThickness: 5,
                padding: 40,
                fontFamily: OpenSans_1.OpenSans.FAMILY,
                fontStyle: FontStatics_1.FontStyle.NORMAL,
                fontWeight: FontStatics_1.FontWeight.EXTRA_BOLD
            }),
            pieColor: 0xff0000
        };
        this._fiftyFiftyAvailable = false;
        this._featureWinChoiceAvailable = false;
        this._redSelected = false;
        this._bgViewOriginalPos = [];
        this._choiceViewOriginalPos = [];
        this._fiftyFiftyViewOriginalPos = [];
        this._featureWinViewOriginalPos = [];
        this._gambleIntoBonusAllowed = false;
        this._gambleFiftyFiftyAllowed = false;
        this._collectButtonClicked = false;
        GambleAssetConfig_1.GambleAssetConfig.useDefaultAssets = (config.useDefaultAssets !== undefined ? config.useDefaultAssets : this.DEFAULT_GAMBLE_CONFIG.useDefaultAssets);
        NoLimitGamblePlugin._winLoseTextStyle = (config.winLoseTextStyle ? config.winLoseTextStyle : this.DEFAULT_GAMBLE_CONFIG.winLoseTextStyle);
        NoLimitGambleFeatureWinView_1.NoLimitGambleFeatureWinView.pieColor = (config.pieColor ? config.pieColor : this.DEFAULT_GAMBLE_CONFIG.pieColor);
        NoLimitGamblePlugin.POSITION_OFFSET = (config.positionOffset ? config.positionOffset : NoLimitGamblePlugin.POSITION_OFFSET);
    }
    static get winLoseTextStyle() {
        return this._winLoseTextStyle;
    }
    /**
     * @description To fetch all the plugins.
     */
    static fetchPlugins() {
        for (let plugin of NolimitLauncher_1.NolimitLauncher.plugins) {
            if ((0, ApiPlugin_1.isApiPlugin)(plugin)) {
                NoLimitGamblePlugin.apiPlugIn = plugin;
                NoLimitGamblePlugin._replayMode = (plugin.options.replay !== undefined);
            }
            if ((0, SoundPlugin_1.isSoundPlugin)(plugin)) {
                NoLimitGamblePlugin.sound = plugin;
            }
            if ((0, KeypadPlugin_1.isKeypadPlugin)(plugin)) {
                NoLimitGamblePlugin.keypadPlugin = plugin;
            }
            if ((0, AutoPlayPlugin_1.isAutoPlayPlugin)(plugin)) {
                NoLimitGamblePlugin.autoplay = plugin;
            }
        }
        if (NoLimitGamblePlugin.apiPlugIn == undefined) {
            return Promise.reject(new Error("SlotKeypad is missing  ApiPlugin"));
        }
        if (NoLimitGamblePlugin.sound == undefined) {
            return Promise.reject(new Error("SlotKeypad is missing SoundPlugin"));
        }
        if (NoLimitGamblePlugin.keypadPlugin == undefined) {
            return Promise.reject(new Error("SlotKeypad is missing KeypadPlugin"));
        }
        return Promise.resolve();
    }
    getGameRules() {
        const templateLoader = new TemplateLoader_1.TemplateLoader(NoLimitGamblePlugin.apiPlugIn.resources.getStaticRoot());
        templateLoader.add({
            name: "gamble-rules",
            url: "node_modules/@nolimitcity/slot-gamble/resources/default/templates/gamble-rules.mustache"
        });
        const data = {
            hasGambleChoice: this._gambleFiftyFiftyAllowed && this._gambleIntoBonusAllowed,
            hasFiftyFifty: this._gambleFiftyFiftyAllowed,
            hasGambleIntoBonus: this._gambleIntoBonusAllowed
        };
        return templateLoader.load().then((assets) => {
            for (let asset of assets) {
                if (asset.name == "gamble-rules" && asset.loadedData) {
                    return NoLimitGamblePlugin.apiPlugIn.translations.render(asset.loadedData, data);
                }
            }
            throw new Error("NoLimitGamblePlugin could not load gamble-rules");
        });
    }
    getKeypadGuide() {
        const templateLoader = new TemplateLoader_1.TemplateLoader(NoLimitGamblePlugin.apiPlugIn.resources.getStaticRoot());
        templateLoader.add({
            name: "gui-guide",
            url: "node_modules/@nolimitcity/slot-gamble/resources/default/templates/gui-guide.mustache"
        });
        const data = {
            hasGambleChoice: this._gambleFiftyFiftyAllowed && this._gambleIntoBonusAllowed,
            hasFiftyFifty: this._gambleFiftyFiftyAllowed,
            hasGambleIntoBonus: this._gambleIntoBonusAllowed,
            assetPath: GambleAssetConfig_1.GambleAssetConfig.getResourcePath()
        };
        return templateLoader.load().then((assets) => {
            for (let asset of assets) {
                if (asset.name == "gui-guide" && asset.loadedData) {
                    return NoLimitGamblePlugin.apiPlugIn.translations.render(asset.loadedData, data);
                }
            }
            throw new Error("NoLimitGamblePlugin could not load gui-guide");
        });
    }
    /**
     * @description Initialising.
     */
    init() {
        NoLimitGamblePlugin.fetchPlugins().catch((reason) => {
            return Promise.reject(reason);
        });
        NoLimitGamblePlugin.events = NoLimitGamblePlugin.apiPlugIn.eventSystemFactory.create();
        NoLimitGamblePlugin.apiPlugIn.events.on(APIEventSystem_1.APIEvent.STATE, (state) => this.onState(state));
        NoLimitGamblePlugin.apiPlugIn.events.on(APIEventSystem_1.APIEvent.INIT, (data) => this.onData(data));
        NoLimitGamblePlugin.apiPlugIn.events.on(APIEventSystem_1.APIEvent.GAME, (data) => this.onData(data));
        return Promise.resolve(this);
    }
    /**
     * @description When ready.
     */
    getReady() {
        const imgLoader = new ImgLoader_1.ImgLoader(NolimitApplication_1.NolimitApplication.resourcePath);
        for (let asset of GambleAssetConfig_1.GambleAssetConfig.getImgConfigs()) {
            imgLoader.add(asset.name, asset.url);
        }
        const fontLoader = new FontLoader_1.FontLoader(NolimitApplication_1.NolimitApplication.resourcePath);
        fontLoader.add(OpenSans_1.OpenSans.NORMAL_300);
        fontLoader.add(OpenSans_1.OpenSans.NORMAL_400);
        fontLoader.add(OpenSans_1.OpenSans.ITALIC_600);
        fontLoader.add(OpenSans_1.OpenSans.NORMAL_600);
        fontLoader.add(OpenSans_1.OpenSans.NORMAL_700);
        fontLoader.add(OpenSans_1.OpenSans.NORMAL_800);
        fontLoader.add(OpenSans_1.OpenSans.ITALIC_800);
        const promise = Promise.all([imgLoader.load(), fontLoader.load()]);
        return promise.then(() => this.loadComplete());
    }
    /**
     * @description When about to start.
     */
    getReadyToStart() {
        return new Promise((resolve, reject) => {
            resolve(this);
        });
    }
    /**
     * @description Starting.
     */
    start() {
        return new Promise((resolve, reject) => {
            resolve(this);
        });
    }
    //--- PUBLIC
    /**
     * Starts the gamble feature. The game calls this function to initiate the gamble state sequence. Usually in the "done" slotState after win presentations and such.
     */
    startGamble(forceAutoCollect = false) {
        NoLimitGamblePlugin.apiPlugIn.events.once(APIEventSystem_1.APIEvent.GAME, (data) => this.onGameData(data));
        if ((!NoLimitGamblePlugin.apiPlugIn.gameClientConfiguration.gambleFiftyFiftyAllowed && !NoLimitGamblePlugin.apiPlugIn.gameClientConfiguration.gambleIntoBonusAllowed) || forceAutoCollect) {
            //IF not allowed we auto collect, without triggering GAMBLE event. The event normally shows the gamble plugin and and changes the gui.
            NoLimitGamblePlugin.apiPlugIn.betHandler.gambleBet(true);
        }
        else {
            NoLimitGamblePlugin.apiPlugIn.events.trigger(APIEventSystem_1.APIEvent.GAMBLE);
        }
    }
    /**
     * Convenience method for checking if gamble should be active this round.
     */
    hasGamble() {
        return this._serverData.nextMode === GambleGameMode_1.GambleGameMode.GAMBLE;
    }
    /**
     * @description When loading is completed.
     */
    loadComplete() {
        this._container = new PIXI.Container(); //This is typed as StageLayer, because this is the actual display object added to stage.
        this._container.onResize = () => this.onResize(); //By implementing the StageLayers onResize function, it is only called when it is actually on stage. Instead of using a event listener which will trigger even if it's not on stage.
        this._container.name = "NolimitGambleLayer";
        this._bgView = new NoLimitGambleBgView_1.NoLimitGambleBgView();
        this._choiceView = new NoLimitGambleChoiceView_1.NoLimitGambleChoiceView(() => this.onFiftyFiftyClick(), () => this.onFeatureWinClick());
        this._fiftyFiftyView = new NoLimitGambleFiftyFiftyGambleView_1.NoLimitGambleFiftyFiftyGambleView(() => this.onRedButtonClick(), () => this.onBlackButtonClick());
        this._featureWinView = new NoLimitGambleFeatureWinView_1.NoLimitGambleFeatureWinView(() => this.onSpinButtonFeatureWinClick());
        this._baseView = new NoLimitGambleBaseView_1.NoLimitGambleBaseView(() => this.onGambleButtonClick(), () => this.onCollectButtonClick());
        this._container.addChild(this._bgView, this._choiceView, this._fiftyFiftyView, this._featureWinView, this._baseView);
        // todo find a better solution for this.
        this._bgViewOriginalPos.push(this._bgView.x, this._bgView.y);
        this._choiceViewOriginalPos.push(this._choiceView.x, this._choiceView.y);
        this._fiftyFiftyViewOriginalPos.push(this._fiftyFiftyView.x, this._fiftyFiftyView.y);
        this._featureWinViewOriginalPos.push(this._featureWinView.x, this._featureWinView.y);
        return Promise.resolve(this);
    }
    /**
     * @description Handler for init response event.
     * @param data
     */
    onData(data) {
        this._serverData = data;
        if (data.gambleOptions && data.gambleOptions.FeatureWin) {
            this._featureWinPercentage = 6.1 * data.gambleOptions.FeatureWin;
        }
        if (data.nextPlayerInteraction) {
            this._replayNextPlayerInteraction = data.nextPlayerInteraction;
        }
        this._gambleFiftyFiftyAllowed = NoLimitGamblePlugin.apiPlugIn.gameClientConfiguration.gambleFiftyFiftyAllowed;
        this._gambleIntoBonusAllowed = NoLimitGamblePlugin.apiPlugIn.gameClientConfiguration.gambleIntoBonusAllowed;
        this.setGambleOption();
    }
    setServerDataDefaults(data) {
        //Defaulting to 0
        if (!data.gambleCollect) {
            data.gambleModeChosen = data.gambleModeChosen != undefined ? data.gambleModeChosen : 0;
        }
        return data;
    }
    /**
     * @description Handler for game data event.
     * @param data
     */
    onGameData(data) {
        let index;
        let winPresentation = new gsap_1.TimelineLite();
        if (data.gambleCollect && this._collectButtonClicked) {
            winPresentation.add(() => this.endGambleRound());
            return;
        }
        data = this.setServerDataDefaults(data);
        if (data.mode === GambleGameMode_1.GambleGameMode.GAMBLE) {
            if (data.gambleModeChosen === 0) {
                if ((data.gambleSuccess && this._redSelected) || (!data.gambleSuccess && !this._redSelected)) {
                    index = 0;
                }
                else {
                    index = 1;
                }
                winPresentation.add(this._fiftyFiftyView.playCardFlipAnimation(index, data.gambleSuccess, data.nextMode, data.accumulatedRoundWin));
            }
            else if (data.gambleModeChosen === 1) {
                winPresentation.add(this._featureWinView.onResponseReceived(data.gambleSuccess));
            }
        }
        if (data.nextMode != GambleGameMode_1.GambleGameMode.GAMBLE) {
            winPresentation.add(this.hideAll(0));
        }
        winPresentation.add(() => this.endGambleRound());
        this._collectButtonClicked = false;
    }
    /**
     * @description To handle resizing.
     */
    onResize() {
        this._baseView.onResize();
        // todo find a better solution for this
        let xOffset, yOffset;
        if (NolimitApplication_1.NolimitApplication.isLandscape) {
            xOffset = NoLimitGamblePlugin.POSITION_OFFSET.landscape.x;
            yOffset = NoLimitGamblePlugin.POSITION_OFFSET.landscape.y;
        }
        else {
            xOffset = NoLimitGamblePlugin.POSITION_OFFSET.portrait.x;
            yOffset = NoLimitGamblePlugin.POSITION_OFFSET.portrait.y;
        }
        this._bgView.x = (this._bgViewOriginalPos[0] + xOffset);
        this._bgView.y = (this._bgViewOriginalPos[1] + yOffset);
        this._choiceView.x = (this._choiceViewOriginalPos[0] + xOffset);
        this._choiceView.y = (this._choiceViewOriginalPos[1] + yOffset);
        this._fiftyFiftyView.x = (this._fiftyFiftyViewOriginalPos[0] + xOffset);
        this._fiftyFiftyView.y = (this._fiftyFiftyViewOriginalPos[1] + yOffset);
        this._featureWinView.x = (this._featureWinViewOriginalPos[0] + xOffset);
        this._featureWinView.y = (this._featureWinViewOriginalPos[1] + yOffset);
    }
    /**
     * @description When gamble button is clicked.
     */
    onGambleButtonClick() {
        const soundConfig = {
            name: NoLimitGamblePlugin.CLICK_SOUND,
            loop: false
        };
        NolimitApplication_1.NolimitApplication.events.trigger(GambleEvents_1.GambleEvents.GAMBLE_PLAY_KEYPAD_SOUND, soundConfig);
        this._fiftyFiftyView.allDone();
        this._featureWinView.allDone();
        this._bgView.show();
        if (this._fiftyFiftyAvailable && this._featureWinChoiceAvailable) {
            if (this._fiftyFiftyView.visible) {
                this._fiftyFiftyView.allDone();
            }
            this._choiceView.show();
        }
        else if (this._fiftyFiftyAvailable) {
            this._fiftyFiftyView.show();
            this._baseView.onGambleButtonClick();
        }
        else if (this._featureWinChoiceAvailable) {
            this._featureWinView.show(this._featureWinPercentage);
            this._baseView.onGambleButtonClick();
        }
    }
    /**
     * @description When collect button is clicked.
     */
    onCollectButtonClick() {
        this._baseView.disableButtons();
        const soundConfig = {
            name: NoLimitGamblePlugin.CLICK_SOUND,
            loop: false
        };
        NolimitApplication_1.NolimitApplication.events.trigger(GambleEvents_1.GambleEvents.GAMBLE_PLAY_KEYPAD_SOUND, soundConfig);
        this._collectButtonClicked = true;
        NoLimitGamblePlugin.apiPlugIn.betHandler.gambleBet(true);
    }
    hideAll(holdScreenTime) {
        const tl = new gsap_1.TimelineLite({
            onComplete: () => {
                this._bgView.allDone();
                this._choiceView.allDone();
                this._fiftyFiftyView.allDone();
                this._featureWinView.allDone();
                this._baseView.hide();
                NolimitApplication_1.NolimitApplication.removeLayer(this._container.name);
                NolimitApplication_1.NolimitApplication.events.trigger(GambleEvents_1.GambleEvents.GAMBLE_FINISHED);
            }
        });
        tl.add(() => {
            const soundConfig = {
                name: NoLimitGamblePlugin.GAMBLE_MUSIC,
                loop: true,
                fadeOutDuration: 100
            };
            NolimitApplication_1.NolimitApplication.events.trigger(GambleEvents_1.GambleEvents.GAMBLE_STOP_EFFECT_SOUND, soundConfig);
        }, holdScreenTime);
        tl.to(this._container, 0.2, { alpha: 0 }, holdScreenTime);
        return tl;
    }
    endGambleRound() {
        NoLimitGamblePlugin.apiPlugIn.events.trigger(APIEventSystem_1.APIEvent.GAMBLE_DONE);
    }
    /**
     * @description When fifty fifty view is selected.
     */
    onFiftyFiftyClick() {
        const soundConfig = {
            name: NoLimitGamblePlugin.CLICK_SOUND,
            loop: false
        };
        NolimitApplication_1.NolimitApplication.events.trigger(GambleEvents_1.GambleEvents.GAMBLE_PLAY_KEYPAD_SOUND, soundConfig);
        this._choiceView.allDone();
        this._fiftyFiftyView.show();
    }
    /**
     * @description When red button on fifty fifty view is clicked.
     */
    onRedButtonClick() {
        const soundConfig = {
            name: NoLimitGamblePlugin.CLICK_SOUND,
            loop: false
        };
        NolimitApplication_1.NolimitApplication.events.trigger(GambleEvents_1.GambleEvents.GAMBLE_PLAY_KEYPAD_SOUND, soundConfig);
        NoLimitGamblePlugin.apiPlugIn.betHandler.gambleBet(false, "0", "1");
        this._redSelected = true;
        this._fiftyFiftyView.onRedButtonClick();
        // disable buttons
        this._baseView.onGambleButtonClick();
        this._baseView.disableButtons();
    }
    /**
     * @description When black button on fifty fifty view is clicked.
     */
    onBlackButtonClick() {
        const soundConfig = {
            name: NoLimitGamblePlugin.CLICK_SOUND,
            loop: false
        };
        NolimitApplication_1.NolimitApplication.events.trigger(GambleEvents_1.GambleEvents.GAMBLE_PLAY_KEYPAD_SOUND, soundConfig);
        NoLimitGamblePlugin.apiPlugIn.betHandler.gambleBet(false, "0", "2");
        this._redSelected = false;
        this._fiftyFiftyView.onBlackButtonClick();
        // disable buttons
        this._baseView.onGambleButtonClick();
        this._baseView.disableButtons();
    }
    /**
     * @description When feature win view is selected.
     */
    onFeatureWinClick() {
        const soundConfig = {
            name: NoLimitGamblePlugin.CLICK_SOUND,
            loop: false
        };
        NolimitApplication_1.NolimitApplication.events.trigger(GambleEvents_1.GambleEvents.GAMBLE_PLAY_KEYPAD_SOUND, soundConfig);
        this._choiceView.allDone();
        this._featureWinView.show(this._featureWinPercentage);
    }
    /**
     * @description When spin button on feature win view is clicked.
     */
    onSpinButtonFeatureWinClick() {
        const soundConfig = {
            name: NoLimitGamblePlugin.CLICK_SOUND,
            loop: false
        };
        NolimitApplication_1.NolimitApplication.events.trigger(GambleEvents_1.GambleEvents.GAMBLE_PLAY_KEYPAD_SOUND, soundConfig);
        NoLimitGamblePlugin.apiPlugIn.betHandler.gambleBet(false, "1", "-1");
        this._featureWinView.onSpinButtonClicked();
        // disable buttons
        this._baseView.onGambleButtonClick();
        this._baseView.disableButtons();
    }
    onState(state) {
        switch (state) {
            case SlotStateHandler_1.SlotState.RESTORE:
                this.restore();
                break;
            case SlotStateHandler_1.SlotState.GAMBLE:
                if (!this._baseView.visible && !NoLimitGamblePlugin.autoplay.isAutoplayRound) {
                    NolimitApplication_1.NolimitApplication.events.trigger(GambleEvents_1.GambleEvents.GAMBLE_STARTED);
                    const soundConfig = {
                        name: NoLimitGamblePlugin.GAMBLE_MUSIC,
                        loop: true
                    };
                    NolimitApplication_1.NolimitApplication.events.trigger(GambleEvents_1.GambleEvents.GAMBLE_PLAY_EFFECT_SOUND, soundConfig);
                    this.onResize();
                    this._baseView.show();
                    this._baseView.enableButtons();
                    this._container.alpha = 1;
                    NolimitApplication_1.NolimitApplication.addLayer(this._container.name, this._container);
                    if (NoLimitGamblePlugin._replayMode && this._replayNextPlayerInteraction) {
                        this._baseView.disableButtons();
                        this.handleReplay();
                    }
                }
                else {
                    if (NoLimitGamblePlugin._replayMode && this._replayNextPlayerInteraction) {
                        this._baseView.disableButtons();
                        this.handleReplay();
                    }
                    else if (!NoLimitGamblePlugin.autoplay.isAutoplayRound && this._fiftyFiftyAvailable && this._featureWinChoiceAvailable) {
                        this._baseView.show();
                        this._baseView.enableButtons();
                    }
                    else {
                        this._baseView.enableCollectButton();
                    }
                }
                break;
            case SlotStateHandler_1.SlotState.GAMBLING:
                this._baseView.disableButtons();
                break;
            case SlotStateHandler_1.SlotState.GAMBLE_COLLECT:
                this.hideAll(0);
                break;
        }
    }
    restore() {
        if (this._serverData.mode === GambleGameMode_1.GambleGameMode.GAMBLE || this._serverData.nextMode === GambleGameMode_1.GambleGameMode.GAMBLE) {
            this.setGambleOption();
        }
    }
    /**
     * @description To set whatever gamble options are available.
     */
    setGambleOption() {
        if (this._serverData.gambleOptions != undefined) {
            this._fiftyFiftyAvailable = this._serverData.gambleOptions.FiftyFifty !== undefined && this._gambleFiftyFiftyAllowed;
            this._featureWinChoiceAvailable = this._serverData.gambleOptions.FeatureWin !== undefined && this._gambleIntoBonusAllowed;
        }
        else {
            throw new Error("Missing gambleOptions data");
        }
    }
    handleReplay() {
        const duration = 0.4;
        if (this._replayNextPlayerInteraction.gambleCollected === "true") {
            GSAPCompatabilityWrapper_1.GSAPCompatabilityWrapper.delayedCall(duration, () => this.onCollectButtonClick());
            return;
        }
        if (this._serverData.mode !== GambleGameMode_1.GambleGameMode.GAMBLE && this._serverData.nextMode === GambleGameMode_1.GambleGameMode.GAMBLE) {
            this.handleReplayAsFirstTime(duration);
        }
        else {
            if (this._replayNextPlayerInteraction.gambleOption === "0") {
                if (this._replayNextPlayerInteraction.gambleSelection === "1") {
                    GSAPCompatabilityWrapper_1.GSAPCompatabilityWrapper.delayedCall((duration / 2), () => this.onRedButtonClick());
                }
                else {
                    GSAPCompatabilityWrapper_1.GSAPCompatabilityWrapper.delayedCall((duration / 2), () => this.onBlackButtonClick());
                }
            }
            else {
                this._baseView.show();
                this._baseView.enableButtons();
                this.handleReplayAsFirstTime(duration);
            }
        }
    }
    handleReplayAsFirstTime(duration) {
        GSAPCompatabilityWrapper_1.GSAPCompatabilityWrapper.delayedCall(duration, () => {
            this.onGambleButtonClick();
            this._choiceView.disableButtons();
        });
        if (this._replayNextPlayerInteraction.gambleOption === "0") {
            GSAPCompatabilityWrapper_1.GSAPCompatabilityWrapper.delayedCall((duration + (duration / 2)), () => {
                this.onFiftyFiftyClick();
                this._fiftyFiftyView.disableButtons();
            });
            if (this._replayNextPlayerInteraction.gambleSelection === "1") {
                GSAPCompatabilityWrapper_1.GSAPCompatabilityWrapper.delayedCall((2 * duration), () => this.onRedButtonClick());
            }
            else {
                GSAPCompatabilityWrapper_1.GSAPCompatabilityWrapper.delayedCall((2 * duration), () => this.onBlackButtonClick());
            }
        }
        else if (this._replayNextPlayerInteraction.gambleOption === "1") {
            GSAPCompatabilityWrapper_1.GSAPCompatabilityWrapper.delayedCall((duration + (duration / 2)), () => {
                this.onFeatureWinClick();
                this._featureWinView.disableButtons();
            });
            GSAPCompatabilityWrapper_1.GSAPCompatabilityWrapper.delayedCall((2 * duration), () => this.onSpinButtonFeatureWinClick());
        }
    }
    /**
     * @description Helper method to shrink text via reducing the font.
     * @param text
     * @param textContainer
     * @param maxWidth
     */
    static shrinkTextViaFont(text, textContainer, maxWidth) {
        while (textContainer.width > maxWidth) {
            if (typeof (textContainer.style.fontSize) === "string") {
                if (textContainer.style.fontSize.indexOf("em") > -1) {
                    debugger;
                    throw new Error("Gamble.shrinkTextWidth(), I am lazy so I don't want to parse em, please use number or px.");
                }
                textContainer.style.fontSize = parseInt(textContainer.style.fontSize);
            }
            textContainer.style.fontSize--;
            if (textContainer.style.fontSize <= 10) {
                textContainer.style.fontSize = 10;
                return;
            }
        }
    }
}
exports.NoLimitGamblePlugin = NoLimitGamblePlugin;
// gamble music loop
NoLimitGamblePlugin.GAMBLE_MUSIC = "gamble_music";
// keypad sounds
NoLimitGamblePlugin.CLICK_SOUND = "click";
NoLimitGamblePlugin.GAMBLE_TOGGLE = "gamble-gamble";
NoLimitGamblePlugin.COLLECT_TOGGLE = "gamble-collect";
// effects sounds
NoLimitGamblePlugin.WIN_CARD_SOUND = "gamble_win_card";
NoLimitGamblePlugin.LOSE_CARD_SOUND = "gamble_lose_card";
NoLimitGamblePlugin.SPIN_WHEEL_SOUND = "gamble_spin_wheel";
NoLimitGamblePlugin.WIN_WHEEL_SOUND = "gamble_win_wheel";
// win/lose strings
NoLimitGamblePlugin.WIN_TEXT = "Win";
NoLimitGamblePlugin.LOSE_TEXT = "Lose";
NoLimitGamblePlugin.POSITION_OFFSET = {
    portrait: {
        x: 0,
        y: 0
    },
    landscape: {
        x: 0,
        y: 0
    }
};
//# sourceMappingURL=NoLimitGamblePlugin.js.map